import { config } from '../config';

export const FAST_TRACK_INITIALIZED = 'fast-track:initialized';

export interface IFastTrackConfig {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    baseUrl: string;
    staging: boolean;
    brandName: string;
    integrationVersion: number;
    auth: {
        enableJWT: boolean;
        autoInit: boolean;
        sid?: string;
        source?: string;
    };
    modules: {
        rewards: {
            enable: boolean;
        };
    };
}

declare global {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Window {
        fasttrackbrand?: string;
        sid?: string;
        source?: string;
        fasttrack?: any;
        FastTrackLoader?: any;
        doInit?: (...args: any[]) => Promise<any>;

        initFtRewards?: () => void;
        logoutFtRewards?: () => void;
        updateFtJwtToken?: (token: string) => void;
    }
}

export class FastTrackService {
    initialize: boolean = false;

    constructor() {
        const configs: IFastTrackConfig = (config as any)
            .fastTrack as IFastTrackConfig;

        if (!configs) {
            console.warn('No Fast Track service is found!');
            return;
        }

        if (configs.auth.sid) {
            window.sid = configs.auth.sid;
        }

        if (configs.auth.source) {
            window.source = configs.auth.source;
        }

        window.fasttrackbrand = configs.brandName;

        window.fasttrack = {
            baseUrl: configs.baseUrl,
            enableJWT: configs.auth.enableJWT,
            integrationVersion: configs.integrationVersion,
            autoInit: configs.auth.autoInit,
            rewards: configs.modules.rewards,
            staging: configs.staging,
        };
    }

    private wrapDoInit(): void {
        const originalDoInit = window.doInit;
        if (!originalDoInit) {
            return;
        }

        window.doInit = async () => {
            const result = await originalDoInit();
            window.eventBus.publish(FAST_TRACK_INITIALIZED, true);
            return result;
        };
    }

    /**
     * Loads the Fast Track CRM SDK script and initializes it
     */
    load(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (window.FastTrackLoader) {
                // Fast Track SDK is already loaded
                resolve();
                return;
            }

            const crmScript = document.createElement('script');
            crmScript.id = 'fasttrack-crm-loader';

            // dynamic src based on environment
            const isStaging = window.fasttrack?.staging;
            crmScript.src = `https://lib${
                isStaging ? '-staging' : ''
            }.rewards.tech/loader/fasttrack-crm.js`;
            crmScript.async = true;

            crmScript.onload = () => {
                try {
                    this.wrapDoInit();
                    new window.FastTrackLoader();
                    this.initialize = true;
                    resolve();
                } catch (error) {
                    console.error('Error initializing Fast Track SDK:', error);
                    reject(error);
                }
            };

            crmScript.onerror = () => {
                console.error(
                    '[FastTrackService] Failed to load Fast Track CRM script.',
                );
                reject(new Error('Fast Track script loading failed.'));
            };

            document.body.appendChild(crmScript);
        });
    }

    public async login(token: string): Promise<void> {
        window.sid = token;

        if (!this.initialize) {
            await this.load();
        }
    }

    public logout(): void {
        if (!this.initialize) {
            return;
        }

        window.sid = undefined;
        window.logoutFtRewards?.();
    }
}

export const fastTrack = new FastTrackService();
